import { WikiLayout } from "@/components/WikiLayout";
import { WikiPage } from "@/components/WikiPage";
import { Link } from "react-router-dom";

const Chemicals = () => {
  return (
    <WikiLayout>
      <WikiPage 
        title="Chemical Quick Reference" 
        description="Quick lookup tables for water chemistry targets and dosing"
      >
        <section className="space-y-6">
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
            <p className="text-sm text-muted-foreground">
              📖 For detailed explanations, theory, and troubleshooting, see the{" "}
              <Link to="/wiki/fundamentals/chemistry" className="text-primary hover:underline font-medium">
                Water Chemistry 101
              </Link>{" "}
              guide.
            </p>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Ideal Water Chemistry Ranges</h2>
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left p-3 font-medium">Parameter</th>
                  <th className="text-left p-3 font-medium">Ideal Range</th>
                  <th className="text-left p-3 font-medium">Critical Range</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                <tr>
                  <td className="p-3">Free Chlorine</td>
                  <td className="p-3 text-muted-foreground">1.0-3.0 ppm</td>
                  <td className="p-3 text-destructive">&lt;0.5 or &gt;5.0</td>
                </tr>
                <tr>
                  <td className="p-3">pH</td>
                  <td className="p-3 text-muted-foreground">7.2-7.6</td>
                  <td className="p-3 text-destructive">&lt;7.0 or &gt;8.0</td>
                </tr>
                <tr>
                  <td className="p-3">Total Alkalinity</td>
                  <td className="p-3 text-muted-foreground">80-120 ppm</td>
                  <td className="p-3 text-destructive">&lt;60 or &gt;180</td>
                </tr>
                <tr>
                  <td className="p-3">Calcium Hardness</td>
                  <td className="p-3 text-muted-foreground">200-400 ppm</td>
                  <td className="p-3 text-destructive">&lt;150 or &gt;500</td>
                </tr>
                <tr>
                  <td className="p-3">Cyanuric Acid</td>
                  <td className="p-3 text-muted-foreground">30-50 ppm</td>
                  <td className="p-3 text-destructive">&lt;20 or &gt;100</td>
                </tr>
                <tr>
                  <td className="p-3">Salt (salt pools)</td>
                  <td className="p-3 text-muted-foreground">3000-3500 ppm</td>
                  <td className="p-3 text-destructive">&lt;2700 or &gt;4000</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Quick Dosing Chart</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Standard pool (20,000 gallons) — adjust proportionally for pool size
          </p>
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left p-3 font-medium">To Adjust</th>
                  <th className="text-left p-3 font-medium">Add</th>
                  <th className="text-left p-3 font-medium">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-sm">
                <tr>
                  <td className="p-3">Raise FC by 1 ppm</td>
                  <td className="p-3 text-muted-foreground">Granular chlorine</td>
                  <td className="p-3 text-muted-foreground">2.5 lbs</td>
                </tr>
                <tr>
                  <td className="p-3">Raise FC by 1 ppm</td>
                  <td className="p-3 text-muted-foreground">Liquid chlorine</td>
                  <td className="p-3 text-muted-foreground">1 gallon</td>
                </tr>
                <tr>
                  <td className="p-3">Lower pH by 0.2</td>
                  <td className="p-3 text-muted-foreground">Muriatic acid</td>
                  <td className="p-3 text-muted-foreground">8 oz</td>
                </tr>
                <tr>
                  <td className="p-3">Raise pH by 0.2</td>
                  <td className="p-3 text-muted-foreground">Soda ash</td>
                  <td className="p-3 text-muted-foreground">6 oz</td>
                </tr>
                <tr>
                  <td className="p-3">Raise TA by 10 ppm</td>
                  <td className="p-3 text-muted-foreground">Alkalinity increaser</td>
                  <td className="p-3 text-muted-foreground">1.5 lbs</td>
                </tr>
                <tr>
                  <td className="p-3">Raise CYA by 10 ppm</td>
                  <td className="p-3 text-muted-foreground">Stabilizer</td>
                  <td className="p-3 text-muted-foreground">2 lbs</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Chemical Compatibility</h2>
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
            <p className="font-semibold text-destructive mb-2">⚠️ NEVER MIX</p>
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2">
              <li>Chlorine + Acid = Toxic chlorine gas</li>
              <li>Different types of chlorine = Fire/explosion risk</li>
              <li>Shock + Algaecide at same time = Reduced effectiveness</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-3">
              <strong>Wait 4 hours</strong> between adding different chemicals.
            </p>
          </div>

          <div className="bg-muted/50 rounded-lg p-4 mt-6">
            <p className="text-sm text-muted-foreground">
              📋 Download the full Chemical Safety Data Sheets (SDS) from the{" "}
              <Link to="/wiki/resources/forms" className="text-primary hover:underline">
                Forms & Documents
              </Link>{" "}
              page. Keep a printed copy in your vehicle.
            </p>
          </div>
        </section>
      </WikiPage>
    </WikiLayout>
  );
};

export default Chemicals;
